import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Linkedin, Loader2, Mic, ChevronDown, ChevronUp } from 'lucide-react';
import { ParsedLinkedInMessage, parseLinkedInThread } from '../../utils/linkedinThreadParser';
import { useTimezone } from '../../hooks/useTimezone';
import { supabase } from '../../lib/supabase';
import { toLocaleTimeStringInTz, toLocaleDateStringInTz } from '../../utils/dateFormatters';

interface LinkedInThreadModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId?: string;
  threadText?: string;
  leadName?: string;
  companyName?: string;
  agentName?: string;
}

function formatMsgTime(timestamp: string, tz?: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    const timeString = toLocaleTimeStringInTz(date, tz, 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    if (diffInDays === 0) return `Today at ${timeString}`;
    if (diffInDays === 1) return `Yesterday at ${timeString}`;
    if (diffInDays < 7) {
      const dayName = toLocaleDateStringInTz(date, tz, 'en-US', { weekday: 'long' });
      return `${dayName} at ${timeString}`;
    }

    const dateString = toLocaleDateStringInTz(date, tz, 'en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
    return `${dateString} at ${timeString}`;
  } catch {
    return '';
  }
}

function getMsgDateGroup(timestamp: string, tz?: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';

    return toLocaleDateStringInTz(date, tz, 'en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return '';
  }
}

const LinkedInThreadModal: React.FC<LinkedInThreadModalProps> = ({
  isOpen,
  onClose,
  leadId,
  threadText,
  leadName = 'Lead',
  companyName,
  agentName,
}) => {
  const tz = useTimezone();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<ParsedLinkedInMessage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      return;
    }

    if (leadId) {
      let cancelled = false;
      setLoading(true);

      supabase
        .from('linkedin_logs')
        .select('from_role, message_text, message_type, status, created_at, audio_file')
        .eq('lead_id', leadId)
        .neq('status', 'failed')
        .order('created_at', { ascending: true })
        .then(({ data, error }) => {
          if (cancelled) return;
          if (!error && data && data.length > 0) {
            setMessages(data.map((msg) => {
              const sender: 'user' | 'assistant' = (msg.from_role === 'prospect' || msg.from_role === 'lead') ? 'user' : 'assistant';
              const ts = msg.created_at || '';
              return {
                timestamp: ts,
                formattedDate: formatMsgTime(ts, tz),
                sender,
                body: msg.message_text || '(empty)',
                dateGroup: getMsgDateGroup(ts, tz),
                messageType: msg.message_type || undefined,
                audioUrl: msg.audio_file || null,
              };
            }));
          } else if (threadText) {
            setMessages(parseLinkedInThread(threadText, tz));
          } else {
            setMessages([]);
          }
          setLoading(false);
        });

      return () => { cancelled = true; };
    } else if (threadText) {
      setMessages(parseLinkedInThread(threadText, tz));
    } else {
      setMessages([]);
    }
  }, [isOpen, leadId]);

  useEffect(() => {
    if (isOpen && scrollRef.current && !loading && messages.length > 0) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current!.scrollHeight });
      }, 50);
    }
  }, [isOpen, loading, messages]);

  if (!isOpen) return null;

  const subtitle = companyName
    ? `Conversation with ${leadName} from ${companyName}`
    : `Conversation with ${leadName}`;

  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const leadInitials = getInitials(leadName);

  let lastDateGroup = '';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl shadow-black/20 animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
              <Linkedin className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">LinkedIn Thread</h3>
              <p className="text-xs text-gray-500 font-medium">{subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto custom-scrollbar bg-gradient-to-b from-gray-50/80 to-gray-50"
        >
          {loading ? (
            <div className="text-center py-16">
              <Loader2 size={32} className="mx-auto text-[#1875FF] mb-3 animate-spin" />
              <p className="text-gray-400 text-sm font-medium">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16">
              <Linkedin size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400 text-sm font-medium">No messages to display</p>
            </div>
          ) : (
            <div className="py-5 px-5 space-y-1">
              {messages.map((message, index) => {
                const showDateSeparator =
                  message.dateGroup && message.dateGroup !== lastDateGroup;
                if (message.dateGroup) lastDateGroup = message.dateGroup;

                return (
                  <React.Fragment key={index}>
                    {showDateSeparator && (
                      <DateSeparator label={message.dateGroup} />
                    )}
                    <ChatBubble
                      message={message}
                      leadInitials={leadInitials}
                      leadName={leadName}
                      agentName={agentName}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-white">
          <p className="text-xs text-gray-400 text-center font-medium">
            {messages.length} message{messages.length !== 1 ? 's' : ''} in this conversation
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

const DateSeparator: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-center py-3">
    <div className="flex items-center gap-3 w-full">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest font-sans px-2">
        {label}
      </span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  </div>
);

interface ChatBubbleProps {
  message: ParsedLinkedInMessage;
  leadInitials: string;
  leadName: string;
  agentName?: string;
}

const getInitialsFromName = (name: string) => {
  const parts = name.split('|').pop()?.trim() || name;
  const words = parts.split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  return parts.substring(0, 2).toUpperCase();
};

const ChatBubble: React.FC<ChatBubbleProps> = ({ message, leadInitials, leadName, agentName }) => {
  const isAgent = message.sender === 'assistant';
  const isAudio = message.messageType === 'audio' && message.audioUrl;
  const [showTranscript, setShowTranscript] = useState(false);
  const agentDisplayName = agentName || 'AI Agent';
  const agentInitials = getInitialsFromName(agentDisplayName);

  return (
    <div className={`flex gap-2.5 ${isAgent ? 'flex-row-reverse' : 'flex-row'} mb-3`}>
      <div className="flex-shrink-0 pt-1">
        {isAgent ? (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-md">
            <span className="text-[10px] font-bold text-white tracking-tight">{agentInitials}</span>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center shadow-md">
            <span className="text-[10px] font-bold text-white tracking-tight">
              {leadInitials}
            </span>
          </div>
        )}
      </div>

      <div className={`max-w-[75%] ${isAgent ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className={`flex items-center gap-1.5 mb-1 ${isAgent ? 'flex-row-reverse' : ''}`}>
          <span className="text-[11px] font-semibold text-gray-600">
            {isAgent ? agentDisplayName : leadName}
          </span>
        </div>

        <div
          className={`rounded-2xl px-4 py-2.5 shadow-sm ${
            isAgent
              ? 'bg-gradient-to-br from-[#1875FF] to-[#1466E0] text-white rounded-tr-md'
              : 'bg-[#E9EDEF] text-gray-800 rounded-tl-md'
          }`}
        >
          {isAudio ? (
            <div className="min-w-[240px]">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-5 h-5 rounded-md flex items-center justify-center ${
                  isAgent ? 'bg-white/20' : 'bg-gradient-to-br from-[#05E0D1] to-[#1875FF]'
                }`}>
                  <Mic className="text-white" size={10} />
                </div>
                <span className={`text-[11px] font-semibold ${isAgent ? 'text-white/80' : 'text-gray-600'}`}>
                  Voice Note
                </span>
              </div>
              <audio
                controls
                className="w-full h-8"
                preload="metadata"
                style={{ minWidth: '220px' }}
              >
                <source src={message.audioUrl!} type="audio/mp4" />
                <source src={message.audioUrl!} type="audio/ogg" />
                <source src={message.audioUrl!} type="audio/mpeg" />
              </audio>
              {message.body && message.body !== '(empty)' && (
                <div className="mt-2">
                  <button
                    onClick={() => setShowTranscript(!showTranscript)}
                    className={`flex items-center gap-1 text-[11px] font-medium transition-colors ${
                      isAgent ? 'text-white/70 hover:text-white' : 'text-[#1875FF] hover:text-[#1260CC]'
                    }`}
                  >
                    {showTranscript ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    Voice note transcript
                  </button>
                  {showTranscript && (
                    <p className={`mt-1.5 text-[12px] leading-relaxed pt-1.5 whitespace-pre-wrap ${
                      isAgent
                        ? 'text-white/60 border-t border-white/15'
                        : 'text-gray-500 border-t border-gray-300/40'
                    }`}>
                      {message.body}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p
              className={`text-[13px] leading-relaxed whitespace-pre-wrap ${
                isAgent ? 'text-white/95' : 'text-gray-700'
              }`}
            >
              {message.body}
            </p>
          )}
        </div>

        {message.formattedDate && (
          <span
            className={`text-[10px] text-gray-400 mt-1 px-1 ${isAgent ? 'text-right self-end' : ''}`}
          >
            {message.formattedDate}
          </span>
        )}
      </div>
    </div>
  );
};

export default LinkedInThreadModal;
